import styled from '@emotion/styled'
import { PlusOutlined, CopyOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { useEffect } from 'react'
import type { WizardFormValues } from '@sportninja/common/features/competition/wizard'
import { initialWizardValues } from '@sportninja/common/features/competition/wizard'
import ChoiceCard from '../components/ChoiceCard'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import { useOrgCompetitions } from '../hooks/useOrgCompetitions'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'

type Props = { orgId: string }

const Step1Create = ({ orgId }: Props) => {
  const { values, setFieldValue } = useFormikContext<WizardFormValues>()
  const { data, isFetching } = useOrgCompetitions(orgId, '')
  const hasCloneableCompetitions = (data?.length ?? 0) > 0
  const cloneDisabled = !isFetching && !hasCloneableCompetitions

  // Flip an already-selected clone back to new once we know nothing can be cloned.
  useEffect(() => {
    if (cloneDisabled && values.creationMethod === 'clone') {
      setFieldValue('creationMethod', 'new')
    }
  }, [cloneDisabled, values.creationMethod, setFieldValue])

  // Switching method must reset the now-inactive branch's fields. Otherwise
  // values typed earlier (e.g. Step 2 info, or a partially-filled clone form)
  // leak into the active branch's payload and show up as stale defaults when
  // the user toggles back.
  const handlePickMethod = (next: 'new' | 'clone') => {
    if (values.creationMethod === next) {
      return
    }
    setFieldValue('creationMethod', next)
    if (next === 'new') {
      setFieldValue('cloneSource', initialWizardValues.cloneSource)
    } else {
      // 'clone' — clear the new-flow inputs (info + downstream Step 3–5 picks).
      setFieldValue('info', initialWizardValues.info)
      setFieldValue('competitionType', initialWizardValues.competitionType)
      setFieldValue('isTournament', initialWizardValues.isTournament)
      setFieldValue('structureType', initialWizardValues.structureType)
      setFieldValue('divisions', initialWizardValues.divisions)
      setFieldValue('conferences', initialWizardValues.conferences)
      setFieldValue('joinType', initialWizardValues.joinType)
    }
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          How Would You Like to Create This {competitionWord()}?
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Select whether to build a new {competitionWord({ capitalize: false })}{' '}
          or clone an existing one.
        </Text>
      </Header>
      <Grid>
        <ChoiceCard
          icon={<PlusOutlined style={{ fontSize: 40 }} />}
          title={`New ${competitionWord()}`}
          description={`Start a new ${competitionWord({
            capitalize: false,
          })} from scratch`}
          selected={values.creationMethod === 'new'}
          onClick={() => handlePickMethod('new')}
          testId='choice-new'
        />
        <ChoiceCard
          icon={<CopyOutlined style={{ fontSize: 40 }} />}
          title={`Clone ${competitionWord()}`}
          description={
            cloneDisabled
              ? `No existing ${competitionWord({
                  capitalize: false,
                })} to clone`
              : `Copy structure from an existing ${competitionWord({
                  capitalize: false,
                })}`
          }
          selected={values.creationMethod === 'clone'}
          onClick={() => handlePickMethod('clone')}
          disabled={cloneDisabled}
          testId='choice-clone'
        />
      </Grid>
    </Container>
  )
}

export default Step1Create

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  max-width: 720px;

  & > button {
    height: 246px;
  }
`
